import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { TitledInputComponent } from '../titled-input/titled-input.component';
import {SelectItem, SelectMenuComponent, SelectMode} from '../select-menu/select-menu.component';

@Component({
  selector: 'app-paginator',
  imports: [MatSelectModule, CommonModule, TitledInputComponent,  SelectMenuComponent],
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss']
})

export class PaginatorComponent implements OnInit, OnChanges {
  @Input() length = 0;
  @Input() pageSizeOptions = [5, 10, 25, 100];
  @Input() pageSize = this.pageSizeOptions[0];
  @Input() mini = false;
  @Input() initialPage = 1;
  @Input() pageSizeDescription = "Page Size";
  @Input() enablePageSizeSelection: boolean = false;
  @Output() page = new EventEmitter<PageEvent>();
  @Input() currentPage = 1;
  @Input() rowControl: boolean = false;
  @Input() rowsTitle: string = '';
  @Output() rowsChange = new EventEmitter<number | null>();

  pages: (number | string)[] = [];
  rowsOptions: SelectItem<number>[] = [
    { label: '5', key: 5 },
    { label: '25', key: 25 },
    { label: '50', key: 50 },
  ];
  rowsControl: FormControl<number | null> = new FormControl(this.rowsOptions[0].key);

  ngOnInit() {
    this.currentPage = this.initialPage;
    this.generatePages();
  }

  ngOnChanges() {
    this.generatePages();
  }

  generatePages() {
    const pages: (number | string)[] = [];

    if (this.totalPages <= 7) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      } else if (this.currentPage > this.totalPages - 4) {
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    this.pages = pages;
  }

  get totalPages() {
    return Math.ceil(this.length / this.pageSize);
  }

  goToPage(page: number | string) {
    if (typeof page === 'number' && page !== this.currentPage) {
      this.page.emit({
        pageSize: this.pageSize,
        pageIndex: page - 1,
        length: this.length,
        previousPageIndex: this.currentPage
      });
      this.currentPage = page;
      this.generatePages();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  get currentLocation(): string {
    return `${(this.currentPage - 1) * this.pageSize + 1}-${Math.min(this.currentPage * this.pageSize, this.length)} of ${this.length}`;
  }

  selectPageSize(value: number) {
    this.pageSize = value;
    this.currentPage = 1;
    this.page.emit({
      pageSize: this.pageSize,
      pageIndex: this.currentPage,
      length: this.length
    });
  }

  changeRowsNumber(value: number | null) {
    if (this.rowsControl) {
      this.rowsControl.setValue(value);
    }
    this.rowsChange.emit(value);
  }

  protected readonly SelectMenuComponent = SelectMenuComponent;
  protected readonly SelectMode = SelectMode;
}
